import CustomModal from '@components/CustomModal';
import { graphQlClient } from '@config/graphqlClient';
import DocumentsGraphQL from '@graphql/document.queries';
import DocumentsPage from '@pages/documents';
import { Editor } from '@tinymce/tinymce-react';
import { BROKEN_LINK_TAG, tagGenerator } from '@utils/enhanceHtml';
import { useEffect, useRef, useState } from 'react';

export interface TinyMCEProps {
  value: string | undefined;
  editorRef: any;
  height?: number;
  handleDescriptionChange: (description: string) => void;
}

const TinyMCE: React.FC<TinyMCEProps> = ({ value, editorRef, height, handleDescriptionChange }) => {
  const [documentsModal, setDocumentsModal] = useState(false);
  const [initialFileForUpload, setInitialFileForUpload] = useState<File | null>(null);
  // Track which document IDs have been fetched to prevent duplicate API calls
  const fetchedDocIdsRef = useRef<Set<string>>(new Set());

  const baseURL = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/`;

  // Prevent MUI Drawer/Modal from blocking focusin on TinyMCE dialogs
  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.tox-tinymce, .tox-tinymce-aux, .moxman-window, .tam-assetmanager-root')) {
        e.stopImmediatePropagation();
      }
    };

    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);

  const toggleDocumentsModal = () => {
    setDocumentsModal(!documentsModal);
  };

  const updatedFile = (id: string) => {
    setInitialFileForUpload(null);
    toggleDocumentsModal();
    editorRef.current.insertContent(
      '<p>&nbsp;</p><div class="linked-document" id=' + id + '>' + id + '</div><p>&nbsp;</p>',
    );
  };

  // Helper function to extract document ID from S3 URL
  const extractDocumentIdFromS3Url = (url: string): string | null => {
    const match = url.match(/pp-init-s3-bucket\.s3\.amazonaws\.com\/[^/]+\/([^/"]+)/);
    return match ? match[1] : null;
  };

  const addCustomButton = (editor: any) => {
    editor.ui.registry.addButton('customInsertButton', {
      text: 'Files',
      icon: 'paste',
      onAction: () => {
        toggleDocumentsModal();
      },
    });
  };

  const processCustomTags = (editor: any) => {
    const linkedDocs = editor.getDoc().querySelectorAll('.linked-document');
    const linkedDocsArray = Array.from(linkedDocs);
    if (!linkedDocsArray.length) {
      return;
    }
    // eslint-disable-next-line
    linkedDocsArray.map((lnkdoc: any) => {
      const id = lnkdoc.getAttribute('id');
      if (!id) return;
      // Skip if already fetched or pending to prevent duplicate API calls
      if (fetchedDocIdsRef.current.has(id)) return;
      fetchedDocIdsRef.current.add(id);

      graphQlClient
        .request(DocumentsGraphQL.queries.getDocument, { id })
        .then((res: any) => {
          const auxData = res.getDocument;
          lnkdoc.innerHTML = tagGenerator(auxData?.fileType, auxData?.uri, auxData?.name, auxData?.documentId);
        })
        .catch((err: any) => {
          console.error(err);
          lnkdoc.innerHTML = BROKEN_LINK_TAG;
        });
    });
  };

  function cleanLinkedDocumentContent(content: any) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    // Find all linked-document class and empty their inner HTML
    const linkedDocs = doc.querySelectorAll('.linked-document');
    linkedDocs.forEach((el) => {
      // Only process divs that have an ID attribute
      const id = el.getAttribute('id');
      if (id) {
        // Keep the div but ensure innerHTML matches the ID (for consistency)
        el.innerHTML = id;
      } else {
        // Remove divs without ID (they're broken/invalid)
        el?.parentNode?.removeChild(el);
      }
    });
    return doc.body.innerHTML;
  }

  return (
    <>
      <Editor
        tinymceScriptSrc={baseURL + 'tinymce/tinymce.min.js'}
        // CHECK: If the implementation needs further licensing
        licenseKey="gpl"
        onInit={(_evt, editor) => (editorRef.current = editor)}
        value={value}
        init={{
          height: height || '80vh',
          menubar: false,
          promotion: false,
          plugins: [
            'advlist',
            'autolink',
            'lists',
            'link',
            'image',
            'charmap',
            'preview',
            'anchor',
            'searchreplace',
            'visualblocks',
            'code',
            'fullscreen',
            'insertdatetime',
            'media',
            'table',
            'code',
            'help',
            'wordcount',
          ],
          toolbar:
            'undo redo | blocks | ' +
            'bold italic forecolor | ' +
            'bullist numlist outdent indent | customInsertButton | ' +
            'alignleft aligncenter alignright alignjustify | ' +
            'link unlink | removeformat | help',
          link_default_target: '_blank',
          link_target_list: false,
          link_default_rel: 'noopener noreferrer',
          // Prevent TinyMCE from converting absolute URLs to relative
          relative_urls: false,
          remove_script_host: false,
          convert_urls: true,
          image_class_list: [{ title: 'Small', value: 'small-image' }],
          content_style:
            'body { font-family:Helvetica,Arial,sans-serif; font-size:14px } img { max-width: 300px; max-height: 300px; }',
          toolbar_mode: 'sliding',
          setup: function (editor) {
            editor.on('SetContent', function () {
              processCustomTags(editor);
              //editor.focus();
              const body = editor.getBody();
              const lastParagraph = body.querySelectorAll('p:last-of-type')[0];
              editor.selection.setCursorLocation(lastParagraph, 0);
            });
            // To handle pasting of files
            editor.on('paste', function (e) {
              const clipboardData = e.clipboardData;
              const items = clipboardData?.items;
              if (!items) {
                return;
              }
              let containsFile = false;
              for (let i = 0; i < items.length; i++) {
                if (items[i].kind === 'file') {
                  containsFile = true;
                  const blob = items[i].getAsFile();
                  toggleDocumentsModal();
                  setInitialFileForUpload(blob);
                }
              }
              // This block the rest if the clipboard contains a file, otherwise proceed (so we can paste text)
              if (containsFile) {
                e.preventDefault();
                return;
              }

              // Check for pasted HTML with linked-document divs or S3 images
              const html = clipboardData.getData('text/html');
              if (html) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                // First check for linked-document div (which might have img inside)
                const linkedDocDiv = doc.querySelector('.linked-document');
                if (linkedDocDiv) {
                  const documentId = linkedDocDiv.getAttribute('id');
                  if (documentId) {
                    e.preventDefault();
                    editor.insertContent(
                      '<p>&nbsp;</p><div class="linked-document" id=' + documentId + '>' + documentId + '</div><p>&nbsp;</p>',
                    );
                    return;
                  }
                }

                // Otherwise check for standalone img with S3 URL
                if (html.includes('<img')) {
                  const img = doc.querySelector('img');
                  if (img && img.src && img.src.includes('pp-init-s3-bucket')) {
                    const documentId = extractDocumentIdFromS3Url(img.src);
                    if (documentId) {
                      e.preventDefault();
                      editor.insertContent(
                        '<p>&nbsp;</p><div class="linked-document" id=' + documentId + '>' + documentId + '</div><p>&nbsp;</p>',
                      );
                    }
                  }
                }
              }
            });
            // To handle drag and drop of files
            editor.on('drop', function (e) {
              e.preventDefault();
              const dataTransfer = e.dataTransfer;
              if (dataTransfer && dataTransfer.files && dataTransfer.files.length > 0) {
                const file = dataTransfer.files[0];
                toggleDocumentsModal();
                setInitialFileForUpload(file);
              }
            });

            // This event is triggered before the content is saved
            editor.on('SaveContent', function (e) {
              e.content = cleanLinkedDocumentContent(e.content);
            });
            addCustomButton(editor);
          },
        }}
        onEditorChange={handleDescriptionChange}
      />
      <CustomModal isOpen={documentsModal} onClose={toggleDocumentsModal}>
        <DocumentsPage isModal={true} updatedFile={updatedFile} initialFile={initialFileForUpload} />
      </CustomModal>
    </>
  );
};

export default TinyMCE;
